import type { UIMessage } from "../lib/types";

export interface ScheduledTurnAdmission {
  companyId: string;
  message: { id: string; companyId: string; role: "user"; content: string; files?: string[]; createdAt: string };
  attempt: string;
  startedAt: string;
}

const isEmpty = (message: UIMessage) => !(
  message.content || message.thinking || message.tools?.length || message.files?.length
);

/** Only an explicit, durably admitted server turn may create a live bubble.
 * Ordinary orphan content/replayed done events must never create one. */
export function admitScheduledTurn(
  messages: UIMessage[], streamingId: string | null, queuedUserId: string | undefined,
  admission: ScheduledTurnAdmission, placeholderId: string,
) {
  const observed = messages.find((message) => message.scheduledTurn?.attempt === admission.attempt);
  if (observed) return observed.isStreaming ? null : { completed: true as const };
  // History can already contain this attempt's reply when a fresh connection
  // replays its admission before the closing done. Do not revive that turn.
  const startedAt = Date.parse(admission.startedAt);
  const userIndex = messages.findIndex((message) => message.id === admission.message.id);
  if (userIndex !== -1 && messages.slice(userIndex + 1).some((message) => message.role === "assistant" && !message.isStreaming &&
    !message.scheduledTurn && message.id.startsWith("a-") && !isEmpty(message) &&
    Date.parse(message.createdAt) >= startedAt)) return { completed: true as const };

  let next = [...messages];
  let currentIndex = next.findIndex((message) => message.id === streamingId && message.isStreaming);
  let unstagedQueuedHead = false;
  // The legacy done handler anticipates the next LOCAL queued human. A
  // scheduled row can actually be ahead of it in the durable server FIFO.
  // Return only that untouched speculative pair to its chip, so scheduled
  // output cannot consume the human's chip or appear under the wrong user.
  if (currentIndex > 0 && queuedUserId && next[currentIndex - 1]?.id === queuedUserId && isEmpty(next[currentIndex]!)) {
    next.splice(currentIndex - 1, 2);
    currentIndex = -1;
    unstagedQueuedHead = true;
  }

  // History/status recovery can append an empty placeholder after an older
  // interrupted input before this admission arrives. It is NOT an optimistic
  // human POST: keep that durable user in place and discard only its unbound,
  // empty recovery placeholder. Otherwise the FIFO race branch below moves
  // this new turn before the old input and leaves its spinner alive after Done.
  const recovery = next[currentIndex];
  if (currentIndex > 0 && next[currentIndex - 1]?.role === "user" &&
      next[currentIndex - 1]?.id !== admission.message.id && recovery &&
      !recovery.scheduledTurn && /^(recover|restored|resync)-/.test(recovery.id) && isEmpty(recovery)) {
    next.splice(currentIndex, 1);
    currentIndex = -1;
  }

  const current = next[currentIndex];
  const previous = next[currentIndex - 1];
  const canReuse = current && isEmpty(current) && (previous?.role !== "user" || previous.id === admission.message.id);
  const placeholder: UIMessage = {
    ...(canReuse ? current : { id: placeholderId, role: "assistant" as const, content: "", tools: [] }),
    createdAt: admission.startedAt, isStreaming: true,
    scheduledTurn: { userMessageId: admission.message.id, attempt: admission.attempt },
  };
  // A racing optimistic human POST can be queued by the server after this
  // scheduled dispatch. Keep its own pair intact for the POST's reconciliation,
  // but insert this authoritative turn before it, with a separate placeholder.
  const insertion = canReuse ? currentIndex : currentIndex > 0 && previous?.role === "user" && isEmpty(current!)
    ? currentIndex - 1 : next.length;
  if (canReuse) next.splice(currentIndex, 1);
  const user: UIMessage = { ...admission.message, role: "user" };
  const addition = next.some((message) => message.id === user.id) ? [placeholder] : [user, placeholder];
  next.splice(insertion, 0, ...addition);
  return { messages: next, streamingMessageId: placeholder.id, unstagedQueuedHead };
}
